/**
 * Single source of truth for the permission catalogue.
 *
 * Adding a new gated area = add a key here, add it to a role in
 * `migrations/`, and reference it from `src/routes.tsx`. Nothing else.
 *
 * Convention: "<domain>.<action>". A holder of "<domain>.*" gets every
 * action in that domain; a holder of "*" gets everything (owner only).
 */
export const PERMISSIONS = {
  "admin.access": "Open the admin panel",
  "users.read": "View users",
  "users.write": "Create, edit, suspend and delete users",
  "roles.read": "View roles and their permissions",
  "roles.write": "Create and edit roles, assign permissions",
  "audit.read": "View the audit log",
  "sketches.read": "View unpublished sketches",
  "sketches.write": "Create, edit and publish p5 sketches",
  "posts.read": "View unpublished blog posts",
  "posts.write": "Create, edit and publish blog posts",
  "settings.write": "Change site settings, including the background",
  "spotify.write": "Connect Spotify and choose what it shows publicly",
  "alliance.read": "View Alliance sign-ups, including their email addresses",
  "alliance.write": "Remove Alliance sign-ups",
  "visits.read": "View the visit log, including visitor IP addresses",
} as const;

export type Permission = keyof typeof PERMISSIONS;

export const ALL_PERMISSIONS = Object.keys(PERMISSIONS) as Permission[];

export const WILDCARD = "*";

/** Groups the admin UI uses to render permission checkboxes. */
export const PERMISSION_GROUPS: Record<string, Permission[]> = ALL_PERMISSIONS.reduce(
  (acc, p) => {
    const domain = p.split(".")[0]!;
    (acc[domain] ??= []).push(p);
    return acc;
  },
  {} as Record<string, Permission[]>,
);

/**
 * Resolve whether an effective permission set satisfies `needed`.
 * Works identically on the Worker (authorisation) and in React (nav gating).
 */
export function can(granted: readonly string[], needed: Permission | Permission[]): boolean {
  const list = Array.isArray(needed) ? needed : [needed];
  if (list.length === 0) return true;
  const set = new Set(granted);
  if (set.has(WILDCARD)) return true;
  return list.every((perm) => {
    if (set.has(perm)) return true;
    const domain = perm.split(".")[0]!;
    return set.has(`${domain}.*`);
  });
}

/** True if the set satisfies *any* of the needed permissions. */
export function canAny(granted: readonly string[], needed: Permission[]): boolean {
  return needed.length === 0 || needed.some((p) => can(granted, p));
}
